/**
 * HIDRATAÇÃO DO CONTEXTO DOCUMENTAL — reabrir um documento salvo sem perder
 * o que foi gravado nele.
 *
 * O problema, encontrado no Relatório ME e repetido nos demais: ao abrir um
 * documento existente, a página restaurava só o Cliente e a OS. Projeto,
 * Unidade/Área, Sub-OS, Equipamento e Inspeção **ficavam de fora da seleção
 * e suas listas nem eram carregadas** — os seletores apareciam vazios ainda
 * que o documento carregasse os IDs. E, como a cascata limpa tudo abaixo do
 * campo alterado, bastava tocar em um seletor para os valores gravados serem
 * apagados de vez.
 *
 * A cascata do USUÁRIO e a hidratação são operações diferentes:
 *
 *   CASCATA       o usuário TROCOU o Projeto ⇒ Unidade, Ativo e Inspeção
 *                 escolhidos para o Projeto anterior não valem mais e são
 *                 limpos. Está certo.
 *   HIDRATAÇÃO    o sistema está REPONDO o que o documento já tinha ⇒ nada
 *                 abaixo pode ser limpo, porque nada foi trocado.
 *
 * Usar o caminho da cascata para hidratar é o defeito. Aqui o valor de cada
 * nível só sai quando **ele próprio** não existe mais na sua própria lista.
 *
 * ── LEITURA QUE FALHOU NÃO APAGA NADA ─────────────────────────────────────
 *
 * Se a lista de um nível não pôde ser lida (403, erro técnico), o id gravado
 * é **mantido** e o nível é marcado `UNAVAILABLE`. Limpar aí seria apagar o
 * contexto de um documento por causa de uma permissão — e a primeira
 * gravação seguinte tornaria a perda permanente. Mesma razão do
 * `context-load.ts`: não poder ver não é não existir.
 */

import type { ContextList } from "./context-load.js";

export const HydrationOutcome = {
  /** O id gravado foi encontrado na lista e está reposto. */
  Restored: "RESTORED",
  /** O documento não tinha id gravado neste nível. */
  Empty: "EMPTY",
  /** Havia id gravado, mas ele não está mais na lista. */
  Missing: "MISSING",
  /** A lista não pôde ser lida; o id gravado foi PRESERVADO. */
  Unavailable: "UNAVAILABLE"
} as const;
export type HydrationOutcome = (typeof HydrationOutcome)[keyof typeof HydrationOutcome];

export interface HydrationLevelResult {
  /** Rótulo do nível, como aparece para quem lê: "o Projeto". */
  label: string;
  outcome: HydrationOutcome;
  /** Valor resolvido — o que deve ficar na seleção. */
  value: string;
  /** O id que estava gravado, para a mensagem poder citá-lo. */
  persistedId: string;
}

export interface HydrationReport {
  levels: HydrationLevelResult[];
  /** Algum nível gravado não pôde ser reposto. */
  hasIssues: boolean;
  /** Mensagens prontas para a tela, sem repetição. */
  messages: string[];
}

/**
 * Um nível da cascata. `load` só é chamado depois de os níveis acima terem
 * sido aplicados, de modo que a consulta enxergue o contexto já resolvido.
 */
export interface HydrationLevel<T extends { id: string }> {
  label: string;
  persistedId: string | null | undefined;
  load: () => Promise<ContextList<T>>;
  /** Grava o valor resolvido e a lista carregada na página. */
  apply: (value: string, options: readonly T[]) => void;
  /**
   * Quando o nível é opcional, um id ausente da lista não é problema a
   * relatar — só volta vazio. Usado pela Inspeção do ME, que é vínculo
   * opcional (CLAUDE.md §41).
   */
  optional?: boolean;
}

const text = (value: unknown): string => (value === null || value === undefined ? "" : String(value));

/**
 * Decide o valor de UM nível. Exportada porque é a regra inteira: o id sai
 * apenas quando a lista foi lida com sucesso e ele não está lá.
 */
export function resolveHydrationLevel<T extends { id: string }>(
  persistedId: string | null | undefined,
  list: ContextList<T>,
  label: string
): HydrationLevelResult {
  const id = text(persistedId);
  if (!id) return { label, outcome: HydrationOutcome.Empty, value: "", persistedId: "" };
  if (list.status !== "ok") {
    // Preserva. Ver o bloco "LEITURA QUE FALHOU NÃO APAGA NADA".
    return { label, outcome: HydrationOutcome.Unavailable, value: id, persistedId: id };
  }
  const found = list.items.some(item => text(item.id) === id);
  return found
    ? { label, outcome: HydrationOutcome.Restored, value: id, persistedId: id }
    : { label, outcome: HydrationOutcome.Missing, value: "", persistedId: id };
}

function messageFor(level: HydrationLevelResult): string | null {
  if (level.outcome === HydrationOutcome.Missing) {
    return `${level.label} gravado neste documento não está mais disponível (${level.persistedId}). Escolha outro ou peça a reativação do cadastro.`;
  }
  if (level.outcome === HydrationOutcome.Unavailable) {
    return `Não foi possível conferir ${level.label} gravado neste documento. O valor foi preservado, mas a lista não pôde ser lida — o campo pode aparecer sem opções.`;
  }
  return null;
}

/**
 * Percorre a cascata de cima para baixo, repondo o que o documento gravou.
 *
 * A ordem importa: cada `load` roda depois de o nível acima ter sido
 * aplicado, para a consulta já ver o Cliente/Projeto resolvido. Nenhum nível
 * limpa o de baixo — só o próprio valor, e só quando ele realmente sumiu.
 */
export async function hydrateDocumentContext(
  levels: ReadonlyArray<HydrationLevel<{ id: string }>>
): Promise<HydrationReport> {
  const results: HydrationLevelResult[] = [];
  for (const level of levels) {
    const list = await level.load();
    const result = resolveHydrationLevel(level.persistedId, list, level.label);
    level.apply(result.value, list.items);
    if (!(level.optional && result.outcome === HydrationOutcome.Missing)) {
      results.push(result);
    } else {
      results.push({ ...result, outcome: HydrationOutcome.Empty, persistedId: "" });
    }
  }
  const messages = [...new Set(results.map(messageFor).filter((m): m is string => m !== null))];
  return { levels: results, hasIssues: messages.length > 0, messages };
}

/**
 * VERIFICAÇÃO de um contexto que a página JÁ repôs sozinha.
 *
 * Nem toda tela precisa que a hidratação carregue por ela: o Checklist e o
 * RDO já restauram os ids gravados e já têm as listas na mão, com uma ordem
 * de carregamento própria que funciona. O que falta nelas é a conferência —
 * **ninguém verifica se o id reposto ainda existe na lista**.
 *
 * Isso não é detalhe: atribuir `select.value = "PRJ-7"` quando não há
 * `<option value="PRJ-7">` deixa o select em `""` **em silêncio**. O campo
 * aparece em branco como se nunca tivesse sido preenchido e, ao salvar, o
 * vínculo gravado é apagado — sem erro, sem aviso, sem o usuário saber.
 *
 * Esta função aplica a MESMA regra de `resolveHydrationLevel` sobre listas
 * já carregadas, de forma síncrona, sem exigir que a página reorganize o
 * seu carregamento.
 */
export function verifyRestoredContext(
  levels: ReadonlyArray<{
    label: string;
    persistedId: string | null | undefined;
    options: readonly { id: string }[];
    /** Desfecho da leitura da lista, quando a página o conhece. */
    status?: ContextList<unknown>["status"];
    optional?: boolean;
  }>
): HydrationReport {
  const results: HydrationLevelResult[] = levels.map(level => {
    const list = {
      items: level.options,
      status: level.status ?? "ok",
      message: "",
      placeholder: ""
    } as ContextList<{ id: string }>;
    const result = resolveHydrationLevel(level.persistedId, list, level.label);
    return level.optional && result.outcome === HydrationOutcome.Missing
      ? { ...result, outcome: HydrationOutcome.Empty, persistedId: "" }
      : result;
  });
  const messages = [...new Set(results.map(messageFor).filter((m): m is string => m !== null))];
  return { levels: results, hasIssues: messages.length > 0, messages };
}

/** Os níveis cujo valor gravado NÃO foi encontrado — os que bloqueiam gravar. */
export function missingRestoredLevels(report: HydrationReport | null): HydrationLevelResult[] {
  return (report?.levels ?? []).filter(level => level.outcome === HydrationOutcome.Missing);
}

/** O aviso pronto, no mesmo formato do `context-load`. Vazio quando está tudo reposto. */
export function hydrationBanner(report: HydrationReport | null): string {
  if (!report || !report.hasIssues) return "";
  const escape = (v: string) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `
    <div class="ctx-hydration-issues">
      <strong>CONTEXTO GRAVADO NÃO PÔDE SER TOTALMENTE REPOSTO</strong>
      <ul>${report.messages.map(m => `<li>${escape(m)}</li>`).join("")}</ul>
    </div>`;
}

/** Estilo do aviso — âmbar, porque é advertência, não falha de carregamento. */
export const CONTEXT_HYDRATION_STYLES = `
  .ctx-hydration-issues { border:1px solid rgba(178,148,85,0.45); background: rgba(178,148,85,0.10); color: var(--ia-warning-fg, #d9bd84); border-radius:6px; padding:10px 12px; margin-bottom:14px; font-size:0.78rem; }
  .ctx-hydration-issues strong { display:block; font-size:0.72rem; letter-spacing:0.04em; margin-bottom:6px; }
  .ctx-hydration-issues ul { margin:0; padding-left:18px; line-height:1.6; }
`;
